import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Loader2, Save, Upload, Twitter } from "lucide-react";
import { updateEntityProfile, uploadEntityAvatar, avatarFromX, setEntityWebhook } from "@/lib/api";
import AvatarCropModal from "@/components/AvatarCropModal";
import { toast } from "sonner";

// Edit modal for a verified KOL. Auth is the edit_token issued at verification
// (stored in localStorage), proving X ownership.
export default function EditProfileModal({ entity, editToken, open, onClose, onSaved }) {
  const isToken = entity?.category === "tokens";
  const [form, setForm] = useState({ display_name: "", bio: "", logo: "", webhook_url: "", website: "", twitter: "", telegram: "" });
  const [saving, setSaving] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fromX, setFromX] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && entity) {
      setForm({
        display_name: entity.display_name || "",
        bio: entity.bio || "",
        logo: entity.logo || "",
        webhook_url: "",
        website: entity.website || "",
        twitter: entity.twitter || "",
        telegram: entity.telegram || "",
      });
    }
  }, [open, entity]);

  if (!entity) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setCropSrc(URL.createObjectURL(file));
  };

  const onCropped = async (blob) => {
    const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setUploading(true);
    try {
      const updated = await uploadEntityAvatar(entity.id, file, editToken);
      set("logo", updated.logo);
      onSaved(updated);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const useXPhoto = async () => {
    setFromX(true);
    try {
      const updated = await avatarFromX(entity.id, editToken);
      set("logo", updated.logo);
      onSaved(updated);
      toast.success("Imported your X photo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't import your X photo");
    } finally { setFromX(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { edit_token: editToken, bio: form.bio };
      if (isToken) {
        payload.website = form.website;
        payload.twitter = form.twitter;
        payload.telegram = form.telegram;
      } else {
        payload.display_name = form.display_name;
      }
      const updated = await updateEntityProfile(entity.id, payload);
      toast.success("Profile updated");
      onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    try {
      await setEntityWebhook(entity.id, editToken, form.webhook_url);
      toast.success("Flip alerts saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save webhook");
    } finally { setSavingWebhook(false); }
  };

  return (
    <>
      <Dialog open={open && !cropSrc} onOpenChange={(o) => !o && !saving && !uploading && !fromX && onClose()}>
        <DialogContent className="max-w-md gap-0 overflow-hidden border-white/10 bg-[#0a0a0a] p-0 [&>button]:hidden" data-testid="edit-profile-modal">
          <DialogTitle className="sr-only">Edit profile</DialogTitle>
          <DialogDescription className="sr-only">Update the profile details and notification settings.</DialogDescription>
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div className="font-display text-lg font-black tracking-tight">Edit profile</div>
            <button onClick={() => !saving && !uploading && !fromX && onClose()} className="text-zinc-500 hover:text-white" data-testid="edit-close">
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="space-y-4 px-5 py-5">
            <div className="flex items-center gap-4">
              {form.logo
                ? <img src={form.logo} alt="" className="h-16 w-16 rounded-full border border-white/10 object-cover" />
                : <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-zinc-900 font-display text-lg font-black">{(entity.ticker || entity.display_name || entity.handle || "?").slice(0, 2).toUpperCase()}</div>}
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Profile photo</label>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="edit-avatar-input" />
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => fileRef.current?.click()} disabled={uploading || fromX} data-testid="edit-avatar-upload"
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-black py-2.5 text-[11px] font-bold uppercase tracking-wide text-zinc-200 transition-colors hover:border-white/40 disabled:opacity-40"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                  </button>
                  <button
                    onClick={useXPhoto} disabled={uploading || fromX} data-testid="edit-avatar-from-x"
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-sky-400/30 bg-sky-400/5 py-2.5 text-[11px] font-bold uppercase tracking-wide text-sky-400 transition-colors hover:bg-sky-400/10 disabled:opacity-40"
                  >
                    {fromX ? <Loader2 className="h-4 w-4 animate-spin" /> : <Twitter className="h-4 w-4" />} Use X photo
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-zinc-600">Square crop, auto-resized &amp; compressed. Up to 8MB.</p>
              </div>
            </div>
            {!isToken && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Display name</label>
                <input data-testid="edit-name" value={form.display_name} onChange={(e) => set("display_name", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40" />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{isToken ? "Project description" : "Bio"}</label>
              <textarea data-testid="edit-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={280}
                className="mt-1 w-full resize-none rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40" />
            </div>
            {isToken && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Website</label>
                  <input data-testid="edit-website" value={form.website} onChange={(e) => set("website", e.target.value)}
                    placeholder="https://…"
                    className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40 placeholder:text-zinc-700" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">X profile</label>
                  <input data-testid="edit-twitter" value={form.twitter} onChange={(e) => set("twitter", e.target.value)}
                    placeholder="https://x.com/…"
                    className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40 placeholder:text-zinc-700" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Telegram</label>
                  <input data-testid="edit-telegram" value={form.telegram} onChange={(e) => set("telegram", e.target.value)}
                    placeholder="https://t.me/…"
                    className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40 placeholder:text-zinc-700" />
                </div>
              </>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Flip alert webhook</label>
              <input data-testid="edit-webhook" value={form.webhook_url}
                onChange={(e) => set("webhook_url", e.target.value)}
                placeholder={entity.webhook_configured ? "Webhook configured — paste a new URL to replace" : "https://hooks.slack.com/… or https://discord.com/api/webhooks/…"}
                className="mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-white/40 placeholder:text-zinc-700" />
              <p className="mt-1 text-[10px] text-zinc-600">Get a POST whenever someone flips your rank. Works with Slack, Discord and Zapier.</p>
              <button data-testid="edit-webhook-save" disabled={savingWebhook} onClick={saveWebhook}
                className="mt-2 rounded-full border border-white/20 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors hover:bg-white/5 disabled:opacity-30">
                {savingWebhook ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : "Save webhook"}
              </button>
            </div>
            <button data-testid="edit-save" disabled={saving} onClick={save}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:opacity-30">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <AvatarCropModal
        imageSrc={cropSrc} open={!!cropSrc}
        onCancel={() => { if (cropSrc) URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
        onCropped={onCropped}
      />
    </>
  );
}
